import Uppy from '@uppy/core';
import AwsS3Multipart from '@uppy/aws-s3';
import { Config } from '@/Config';
import { IImageAsset } from '@/data/models/album';
import CoverArt from './CoverArt';
import { useTranslation } from 'react-i18next';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import './Uploader.scss';
import { assetApiClient } from '@/clients/AssetAPIClient';
import { AssetType } from '@/constants/AssetType';
import Spinner, { SpinnerSize } from '@/components/common/Spinner/Spinner';
import ConfirmationModal from '@/components/common/ConfirmationModal/ConfirmationModal';
import { useErrorContext } from '@/context/ErrorContext';
import { logger } from '@/utils/Logger';
import ImageInfoModal from '../../ImageInfoModal/ImageInfoModal';
import { SignedUrl } from '@/data/models/signedUrl';

interface UploaderConfig {
  albumId: string | undefined;
  asset: IImageAsset | undefined | null;
  assetType: AssetType;
  isAlbumArt: boolean;
  readonly: boolean;
  updateProductAsset: (asset: IImageAsset | null) => Promise<any>;
}

enum UploaderState {
  Empty,
  Uploading,
  Valid,
  LoadingFailed,
  Invalid,
}

export default function Uploader({
  assetType,
  isAlbumArt,
  albumId,
  asset,
  readonly,
  updateProductAsset,
}: UploaderConfig) {
  const showEmptyUploader = () => {
    return !asset;
  };
  const showInvalidUploader = () => {
    return asset?.errors?.length;
  };
  const getCurrentUploaderState = (): UploaderState => {
    return showEmptyUploader()
      ? UploaderState.Empty
      : showInvalidUploader()
        ? UploaderState.Invalid
        : UploaderState.Valid;
  };

  const acceptedImageTypes = ['.jpg', '.jpeg', '.tif', '.tiff', '.png'];

  const [uploaderState, setUploaderState] = useState(getCurrentUploaderState());
  const [uploaderProgress, setUploaderProgress] = useState('0%');
  const [loading, setLoading] = useState(false);
  const [showRemoveAssetModal, setShowRemoveAssetModal] = useState(false);
  const [showArtworkValidationModal, setShowArtworkValidationModal] =
    useState(false);
  const [signedUrl, setSignedUrl] = useState<SignedUrl>();
  const dragEvents = useRef(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();
  const { setError } = useErrorContext();

  useEffect(() => {
    const uploaderState = getCurrentUploaderState();
    setUploaderState(uploaderState);
    if (uploaderState === UploaderState.Valid && asset?.id && albumId) {
      setLoading(true);
      assetApiClient
        .getThumbnailDownloadUrl(asset.id, albumId)
        .then((result) => {
          setSignedUrl({ url: result.signedUrl, mimeType: result.mimeType });
        })
        .catch((e) => {
          setUploaderState(UploaderState.LoadingFailed);
          logger.error(e);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setSignedUrl(undefined);
    }
  }, [asset]);

  const uppy = new Uppy({
    id: 'uppy',
    autoProceed: true,
    allowMultipleUploads: true,
    debug: false,
    restrictions: {
      maxNumberOfFiles: 1,
      minNumberOfFiles: 1,
      allowedFileTypes: acceptedImageTypes,
    },
    meta: {},
  });
  const endpoint = Config.API_URL + '/prs/api/v1/AwsS3MultipartUpload';
  let uploadKey = '';
  let fileName = '';
  uppy.use(AwsS3Multipart, {
    endpoint,
    async createMultipartUpload(file) {
      const uploadId = await assetApiClient
        .createMultipartUploadId()
        .then((response: any) => response.data);
      const filename = `${uploadId}.${file.name.split('.').pop()}`;
      const fileType = file.type;
      const filePath = 'uploads/' + uploadId;
      const res = await assetApiClient
        .initializeMultipartUpload(filename, fileType, filePath)
        .then((response: any) => response.data);
      uploadKey = res.key;
      return { uploadId: res.uploadId, key: res.key };
    },

    async signPart(file, { uploadId, partNumber }) {
      fileName = file.name;
      const res = await assetApiClient
        .getMultipartUploadUrl(uploadId, partNumber, uploadKey)
        .then((response: any) => response.data);
      return { url: res.url };
    },

    async completeMultipartUpload(file, { uploadId, parts }) {
      const data = {
        Parts: parts,
        AssetType: assetType,
        FileName: fileName,
        CreateAsset: true,
      };
      const partsJson = `${JSON.stringify(data)}`;
      await assetApiClient
        .completeMultipartUpload(uploadId, uploadKey, partsJson)
        .then(async (response: any) => {
          await updateProductAsset(response.data);
          return response.data;
        });
      return {};
    },

    async abortMultipartUpload(file, { uploadId, key }) {
      await assetApiClient
        .delete(`/prs/api/v1/AwsS3MultipartUpload/${uploadId}/Abort?key=${key}`)
        .then((response: any) => response.data);
      return;
    },

    shouldUseMultipart() {
      return true;
    },
  });

  uppy.on('upload-progress', (_, progress) => {
    const progressPercent = progress.bytesTotal
      ? Math.floor((progress.bytesUploaded / progress.bytesTotal) * 100)
      : 0;
    setUploaderProgress(`${progressPercent}%`);
  });

  uppy.on('upload-success', (file) => {
    if (file) uppy.removeFile(file.id);
  });

  uppy.on('restriction-failed', () => {
    setError(t('upload.error_file_type_not_supported'));
  });

  uppy.on('upload-error', (_, error) => {
    setError(t('common.error.defaultError'));
    logger.error(error.message);
    uppy.cancelAll();
    setUploaderState(UploaderState.Empty);
  });

  const addUppyFile = (file: File) => {
    uppy.addFile({
      source: 'file input',
      name: file.name,
      type: file.type,
      data: file,
    });
    uppy.upload();
    setUploaderState(UploaderState.Uploading);
  };

  const onFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const target = e.target;
    if (!target?.files || target.files?.length < 1) return;
    addUppyFile(target.files[0]);
    target.value = '';
  };

  const cancelUpload = () => {
    uppy.cancelAll();
    setUploaderState(UploaderState.Empty);
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (dragEvents.current === 0) {
      e.currentTarget.classList.toggle('drag');
    }
    dragEvents.current++;
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragEvents.current--;
    if (dragEvents.current === 0) {
      e.currentTarget.classList.toggle('drag');
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.toggle('drag');
    dragEvents.current = 0;
    if (!e.dataTransfer?.files || e.dataTransfer.files.length < 1) return;
    addUppyFile(e.dataTransfer.files[0]);
  };

  const downloadAsset = () => {
    if (!asset?.id || !albumId) return;
    assetApiClient
      .getAssetDownloadUrl(asset?.id, albumId)
      .then((response: any) => {
        if (!response) {
          return;
        }
        const link = document.createElement('a');
        link.href = response.signedUrl;
        link.download = asset?.displayName ?? 'coverart';
        document.body.appendChild(link);
        link.click();
        link?.parentNode?.removeChild(link);
      })
      .catch(() => {
        setError(t('common.error.defaultError'));
      });
  };

  const removeAsset = async () => {
    setLoading(true);
    try {
      await updateProductAsset(null);
    } catch (error: any) {
      setError(t('common.error.defaultError'));
      logger.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="image-uploader">
      <ConfirmationModal
        showModal={showRemoveAssetModal}
        setShowModal={setShowRemoveAssetModal}
        message={t('upload.remove_asset_confirm_body', {
          imageFilename: asset?.displayName,
        })}
        header={t('upload.remove_asset_confirm_header')}
        handleConfirm={removeAsset}
      ></ConfirmationModal>
      <ImageInfoModal
        asset={asset}
        showModal={showArtworkValidationModal}
        setShowModal={setShowArtworkValidationModal}
      ></ImageInfoModal>
      <div className={`uploader-container${isAlbumArt ? ' album-art' : ''}`}>
        {loading ? (
          <Spinner cover={true} size={SpinnerSize.FullComponent} />
        ) : null}

        {/* <!-- Empty -->*/}
        {uploaderState == UploaderState.Empty ? (
          !readonly ? (
            <div
              className="uploader-content empty active"
              onClick={() => fileInputRef.current?.click()}
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <div className="uploader-text">
                <div className="uploader-text-heading">
                  <button className="btn-upload">
                    <div className="btn-circle-blue">
                      <i className="fas fa-folder icon-folder"></i>
                    </div>
                    <div className="uploader-text-heading">
                      <span className="icon-text">{t('upload.my_device')}</span>
                    </div>
                  </button>

                  <div className="text-block">
                    <div className="text-comments">
                      {t('upload.image_pixels_recommended')}
                    </div>
                    <div className="text-comments">
                      {t('upload.image_minimum')}
                    </div>
                    <div className="text-comments">JPG, PNG, TIFF</div>
                    <div className="text-comments">
                      {t('upload.image_model')}
                    </div>
                  </div>
                </div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                className="secret-input"
                accept={acceptedImageTypes.join(',')}
                onChange={(e) => onFileInputChange(e)}
              />
            </div>
          ) : (
            <div className="uploader-content empty readOnly">
              <i className="icon fas fa-music"></i>
            </div>
          )
        ) : null}

        {/*<!-- Uploading -->*/}
        {uploaderState == UploaderState.Uploading ? (
          <div className="upload-progress" title={uploaderProgress}>
            <div className="progress">
              <div
                className="progress-bar"
                style={{ width: uploaderProgress }}
              ></div>
            </div>
            <div className="uploader-cancel">
              <button className="icon-btn" onClick={cancelUpload}>
                <i className="icon fas fa-times"></i>
              </button>
            </div>
          </div>
        ) : null}

        {/*<!-- Failed to load asset -->*/}
        {uploaderState == UploaderState.LoadingFailed ? (
          <div className="uploader-content">
            <div className="uploader-text">
              <div>{t('upload.unable_to_load_album_art')}</div>
            </div>

            {!readonly ? (
              <div className="image-buttons">
                <button
                  type="button"
                  className="icon-btn delete-button"
                  title="{{ 'upload.remove_asset' | translate }}"
                  onClick={(e) => {
                    setShowRemoveAssetModal(true);
                    e.stopPropagation();
                  }}
                >
                  <i className="icon fas fa-trash"></i>
                </button>
              </div>
            ) : null}
          </div>
        ) : null}

        {/*<!-- Uploaded and valid -->*/}
        {uploaderState == UploaderState.Valid ? (
          <div className="uploader-content valid">
            <div className="uploader-content valid">
              <CoverArt
                asset={asset}
                albumId={albumId}
                signedUrl={signedUrl}
                readonly={readonly}
                downloadAsset={downloadAsset}
                removeAsset={() => setShowRemoveAssetModal(true)}
              />
            </div>
          </div>
        ) : null}

        {/*<!-- Uploaded and invalid/error -->*/}
        {uploaderState === UploaderState.Invalid ? (
          <div
            className="uploader-content invalid"
            onClick={(e) => {
              setShowArtworkValidationModal(true);
              e.stopPropagation();
            }}
          >
            <div className="uploader-text">
              <div className="uploader-text-heading">
                <i className="icon fas fa-exclamation-triangle fa-lg"></i>
              </div>
              <div className="uploader-text-heading">
                <span>{t('upload.invalid_image')}</span>
              </div>
              <div className="uploader-text-normal">
                <span>{t('upload.image_click_for_details')}</span>
              </div>
            </div>

            {/*<!-- COMMON BUTTONS-->*/}
            {!readonly ? (
              <div className="image-buttons">
                <button
                  type="button"
                  className="icon-btn download-button"
                  title={t('common.generic.download')}
                  onClick={async (e) => {
                    e.stopPropagation();
                    await downloadAsset();
                  }}
                >
                  <i className="icon fas fa-arrow-alt-to-bottom"></i>
                </button>
                <button
                  type="button"
                  className="icon-btn delete-button"
                  title={t('upload.remove_asset')}
                  onClick={(e) => {
                    setShowRemoveAssetModal(true);
                    e.stopPropagation();
                  }}
                >
                  <i className="icon fas fa-trash"></i>
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
